import React, { Component } from 'react';
import { connect } from 'react-redux';
import users from '../../images/users.png';
import trendingUp from '../../images/trending-up.png';
import userIcon from '../../images/user.png';
import popinConvo from '../../images/popin-convo.svg';
import './StatsOverview.css';
import { StringUtils } from '../../util/StringUtils';
import { fetchTenantStats } from '../../actions/tenantStats';
import { AnalyticsCardHeader } from './AnalyticsCardHeader';
import TenantStats from '../../models/TenantStats';
import { handleError } from '../../util/Errors';
import Tooltip from '../shared/Tooltip';
import User from '../../models/User';
import Tenant from '../../models/Tenant';
import LoadingAndPulse from '../shared/LoadingAndPulse';
import { arrowDownwardIcon, arrowUpwardIcon } from '../../constants';
import ErrorAndRefresh from '../shared/ErrorAndRefresh';
import { EnterpriseAnalytics } from '../../analytics/EnterpriseAnalytics';
import Logger from '../../util/Logger';
import DateTime from '../../models/DateTime';

const StatsListItem = ({ tenant, tooltip, total, diffCount, diffPercent, title, icon, dateRange, loading, handleMixpanelHover }) => {
  const earliestTime = tenant ? Tenant.getEarliestActivityTime(tenant) : null;
  const activityTooltip = `Trending not available${earliestTime ? ` before ${DateTime.format(
    earliestTime, 'MMM DD, YYYY')}` : '.'}`;
  return (
    <div className="row no-gutters stats-list-item">
      <div className="col-3 d-flex flex-column justify-content-center">
        <div style={ icon } className="icon" />
      </div>
      <div className="col-9">
        <div className="stats-container d-inline-flex flex-column justify-content-center">
          <Tooltip tooltip={ tooltip } callback={ handleMixpanelHover }>
            <div className="row no-gutters align-items-center clickable">
              <h1 className="title mr-1">{ title }</h1>
              <i className="material-icons tooltip-icon">help_outline</i>
            </div>
          </Tooltip>
          <div className="total font-size-xxl">{ loading ? 'Calculating...' : total }</div>
          { (diffCount && diffPercent) ? (
            <h2 className={ 'diff ' + (diffCount > 0 ? 'positive' : 'negative') }>
              <i className="material-icons">{ diffCount > 0
                ? arrowUpwardIcon
                : arrowDownwardIcon }</i>
              <span>{ diffCount } ( { diffPercent > 0 ? '+' + diffPercent : diffPercent }% )</span>
            </h2>
          ) : dateRange.value
            ? (
              <Tooltip tooltip={ activityTooltip }>
                <div className="font-size-sm font-color-med">Trend not available</div>
              </Tooltip>
            )
            // Don't show any delta info if date range is All Time (when dateRange is set to All Time it's value is 0, i.e. 0 weeks in the past)
            : null }
        </div>
      </div>
    </div>
  );
};

class StatsOverview extends Component {

  getColor = (index) => {
    const colors = ['#58C1E3', '#98CF18', '#F5A622', '#1288C6'];
    return colors[index];
  };

  fetchStats = () => {
    const { user, fetchTenantStats, dateRange } = this.props;
    Logger.info('dateRange.value: ', dateRange.value);
    fetchTenantStats(Tenant.getId(User.getTenant(user)), dateRange.value);
  };

  componentDidMount () {
    this.fetchStats();
  }

  componentDidUpdate (prevProps) {
    const { statsError, dateRange } = this.props;

    if (dateRange !== prevProps.dateRange) {
      this.fetchStats();
    }

    handleError(statsError, prevProps.statsError);
  }

  getDivStyle (img, index) {
    const color = this.getColor(index);
    return {
      backgroundImage: `url(${img})`,
      backgroundColor: color
    };
  }

  handleMixpanelHover = (infoIcon) => {
    EnterpriseAnalytics.hoversTooltip(infoIcon);
  };

  render () {
    const { user, statsLoading, stats, prevStats, statsError, dateRange } = this.props;
    const tenant = user ? (User.hasTenant(user) ? User.getTenant(user) : null) : null;
    return (
      <div className="card analytics-card-height card-stats-overview">
        <div className="d-inline-block">
          <AnalyticsCardHeader
            title="OVERVIEW"
            dateRange={ dateRange } />
        </div>

        { statsError
          ? <ErrorAndRefresh error="Failed to fetch Stats" onRefresh={ this.fetchStats } />
          : statsLoading
            ? <LoadingAndPulse size={ 20 } color="#3D9AE2" whatIsLoading="stats" />
            : stats
              ? (
                <div className="row no-gutters top-row-match-height">
                  <div className="col-lg-6 col-md-12">
                    <StatsListItem
                      title="Active Users"
                      total={ StringUtils.roundThousands(TenantStats.getTotalUsers(stats)) }
                      diffCount={ StringUtils.roundThousands(
                        TenantStats.getDiffCountUsers(stats, prevStats)) }
                      diffPercent={ TenantStats.getDiffPercentUsers(stats, prevStats) }
                      icon={ this.getDivStyle(users, 0) }
                      dateRange={ dateRange }
                      tooltip="Anyone who viewed or contributed to a POPin."
                      tenant={ tenant }
                      loading={ statsLoading }
                      handleMixpanelHover={ this.handleMixpanelHover } />
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <StatsListItem
                      title="POPin Creators"
                      total={ StringUtils.roundThousands(TenantStats.getTotalHeroes(stats)) }
                      diffCount={ StringUtils.roundThousands(
                        TenantStats.getDiffCountHeroes(stats, prevStats)) }
                      diffPercent={ TenantStats.getDiffPercentHeroes(stats, prevStats) }
                      icon={ this.getDivStyle(userIcon, 1) }
                      dateRange={ dateRange }
                      tooltip="A POPin Creator is anyone who started a POPin."
                      loading={ statsLoading }
                      tenant={ tenant }
                      handleMixpanelHover={ this.handleMixpanelHover } />
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <StatsListItem
                      title="POPins"
                      total={ StringUtils.roundThousands(TenantStats.getTotalPopins(stats)) }
                      diffCount={ StringUtils.roundThousands(
                        TenantStats.getDiffCountPopins(stats, prevStats)) }
                      diffPercent={ TenantStats.getDiffPercentPopins(stats, prevStats) }
                      tooltip="A POPin contains a question or questions formed by a POPin Creator."
                      tenant={ tenant }
                      icon={ this.getDivStyle(popinConvo, 2) }
                      dateRange={ dateRange }
                      loading={ statsLoading }
                      handleMixpanelHover={ this.handleMixpanelHover } />
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <StatsListItem
                      title="Total Activities"
                      total={ StringUtils.roundThousands(TenantStats.getTotalActivities(stats)) }
                      diffCount={ StringUtils.roundThousands(
                        TenantStats.getDiffCountActivities(stats, prevStats)) }
                      tenant={ tenant }
                      diffPercent={ TenantStats.getDiffPercentActivities(stats, prevStats) }
                      icon={ this.getDivStyle(trendingUp, 3) }
                      dateRange={ dateRange }
                      tooltip="All views and contributions."
                      loading={ statsLoading }
                      handleMixpanelHover={ this.handleMixpanelHover } />
                  </div>
                </div>
              ) : <div>No stats found.</div>
        }
      </div>
    );
  }
}

StatsOverview.propTypes = {};
StatsOverview.defaultProps = {};

const mapStateToProps = state => {
  return {
    user: state.user.user,
    statsLoading: state.tenantStats.statsLoading,
    statsError: state.tenantStats.statsError,
    stats: state.tenantStats.statsOverview,
    prevStats: state.tenantStats.statsOverviewPrev
  };
};

export default connect(mapStateToProps, { fetchTenantStats })(StatsOverview);

